import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import ClrDateRangePicker from './ClrDateRangePicker';

const DATE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'custom', label: 'Custom Range' },
];

function CalendarIcon() {
  return (
    <svg className="clr-icon" width="18" height="18" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M4.5 0.75V3.75M10.5 0.75V3.75M0.75 6.75H14.25M2.25 2.25H12.75C13.5784 2.25 14.25 2.92157 14.25 3.75V14.25C14.25 15.0784 13.5784 15.75 12.75 15.75H2.25C1.42157 15.75 0.75 15.0784 0.75 14.25V3.75C0.75 2.92157 1.42157 2.25 2.25 2.25Z"
        stroke="#5F7470"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatCustomLabel(startDate, endDate) {
  if (!startDate || !endDate) return 'Custom Range';
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'Custom Range';
  return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
}

function ClrDateFilter({
  value,
  onChange,
  title,
  customStartDate,
  customEndDate,
  onCustomRangeApply,
  onCustomRangeReset,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangePanelStyle, setRangePanelStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const rangePanelRef = useRef(null);

  const selectedLabel = useMemo(() => {
    if (value === 'custom') return formatCustomLabel(customStartDate, customEndDate);
    const match = DATE_OPTIONS.find((option) => option.value === value);
    return match?.label || 'Select date range';
  }, [value, customStartDate, customEndDate]);

  useLayoutEffect(() => {
    if (!rangeOpen) {
      setRangePanelStyle(null);
      return undefined;
    }

    const placePanel = () => {
      const trigger = triggerRef.current;
      const panel = rangePanelRef.current;
      if (!trigger || !panel) return;

      const margin = 8;
      const gap = 6;
      const tr = trigger.getBoundingClientRect();
      const pw = panel.offsetWidth || 448;
      const ph = panel.offsetHeight || 280;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let top = tr.bottom + gap;
      let left = tr.left;

      if (left + pw > vw - margin) {
        left = Math.max(margin, vw - pw - margin);
      }
      if (left < margin) left = margin;

      if (top + ph > vh - margin) {
        const above = tr.top - ph - gap;
        top = above >= margin ? above : Math.max(margin, vh - ph - margin);
      }

      setRangePanelStyle({
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        right: 'auto',
        bottom: 'auto',
      });
    };

    placePanel();
    const raf = window.requestAnimationFrame(placePanel);
    window.addEventListener('resize', placePanel);
    window.addEventListener('scroll', placePanel, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', placePanel);
      window.removeEventListener('scroll', placePanel, true);
    };
  }, [rangeOpen]);

  useEffect(() => {
    if (!menuOpen && !rangeOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)
        && !rangePanelRef.current?.contains(event.target)) {
        setMenuOpen(false);
        setRangeOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setRangeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen, rangeOpen]);

  const handleSelect = (nextValue) => {
    if (nextValue === 'custom') {
      onChange(nextValue);
      setMenuOpen(false);
      setRangeOpen(true);
      return;
    }
    onChange(nextValue);
    setMenuOpen(false);
    setRangeOpen(false);
  };

  const handleTriggerClick = () => {
    if (value === 'custom' && rangeOpen) {
      setRangeOpen(false);
      return;
    }
    if (value === 'custom' && !menuOpen) {
      setMenuOpen((prev) => !prev);
      setRangeOpen(false);
      return;
    }
    setMenuOpen((prev) => !prev);
    setRangeOpen(false);
  };

  const handleCustomApply = (start, end) => {
    onCustomRangeApply?.(start, end);
    setRangeOpen(false);
  };

  const handleCustomReset = () => {
    onCustomRangeReset?.();
  };

  return (
    <div
      className={`clr-date-filter${menuOpen || rangeOpen ? ' is-open' : ''}`}
      ref={rootRef}
      title={title}
    >
      <button
        type="button"
        className="clr-date-filter-trigger"
        ref={triggerRef}
        aria-haspopup="listbox"
        aria-expanded={menuOpen || rangeOpen}
        onClick={handleTriggerClick}
      >
        <span className="clr-date-filter-label">{selectedLabel}</span>
        <span className="clr-date-filter-icon">
          <CalendarIcon />
        </span>
      </button>

      {menuOpen && (
        <div className="clr-date-filter-menu" role="listbox">
          {DATE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`clr-date-filter-option${option.value === value ? ' is-selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      <ClrDateRangePicker
        open={rangeOpen}
        panelRef={rangePanelRef}
        panelStyle={rangePanelStyle}
        startDate={customStartDate}
        endDate={customEndDate}
        onApply={handleCustomApply}
        onReset={handleCustomReset}
        onClose={() => setRangeOpen(false)}
      />
    </div>
  );
}

ClrDateFilter.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  customStartDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  customEndDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onCustomRangeApply: PropTypes.func,
  onCustomRangeReset: PropTypes.func,
};

export default ClrDateFilter;
